"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import type { DefaultValues, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItemWithoutDiv,
  UnstyledFormLabel,
} from "@/components/ui/form";

import {
  CurrencyInput,
  floorToTwoDecimals,
  LabelCell,
  NumberDisplayCell,
  sum,
  Table,
  TableHeader,
  Tr,
} from "./shared";

const formSchema = z.object({
  netSellingPrice: z.number().positive(),
  productCosts: z.number().positive(),
  freightCost: z.number().positive(),
  fulfillmentCost: z.number().positive(),
  transactionFees: z.number().positive(),
  adSpend: z.number().positive(),
  fixedOperatingCosts: z.number().positive(),
  unitsSold: z.number().positive(),
  targetOperatingProfitPercentage: z.number().positive(),
});

const defaultValues = {
  netSellingPrice: 100,
  productCosts: 12,
  freightCost: 1,
  fulfillmentCost: 5,
  transactionFees: 1,
} satisfies DefaultValues<z.infer<typeof formSchema>>;

interface ContentProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  transactionFees: number;
  grossProfit: number;
  breakevenMer: number;
  t: ReturnType<typeof useTranslations<"Calculators">>;
  tCommon: ReturnType<typeof useTranslations<"Common">>;
}

export function BreakevenMerCalculator() {
  const t = useTranslations("Calculators");
  const tCommon = useTranslations("Common");
  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const transactionFees = sum(
    form.watch([
      "productCosts",
      "freightCost",
      "fulfillmentCost",
      "transactionFees",
    ])
  );

  const grossProfit = form.watch("netSellingPrice") - transactionFees;
  const breakevenMer = floorToTwoDecimals(
    form.watch("netSellingPrice") / grossProfit
  );

  const tableProps = {
    form,
    transactionFees,
    grossProfit,
    breakevenMer,
    t,
    tCommon,
  } satisfies ContentProps;

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <TableHeader>
          <p className="text-body-md text-text-on-dark sticky top-0 font-semibold">
            {t("breakEvenMer")}
          </p>
        </TableHeader>
        <DesktopTable {...tableProps} />
        <MobileTable {...tableProps} />
      </form>
    </Form>
  );
}

function MobileTable({
  form,
  transactionFees,
  grossProfit,
  breakevenMer,
  t,
  tCommon,
}: ContentProps) {
  const colSpan = 2;

  return (
    <Table className="md:hidden">
      <colgroup>
        <col className="w-1/2" />
        <col className="w-1/2" />
      </colgroup>
      <tbody className="bg-surface">
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={colSpan}>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <Tr>
          <LabelCell colSpan={colSpan} className="italic">
            {t("minusVariableCosts")}
          </LabelCell>
        </Tr>
        <FormField
          control={form.control}
          name="productCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={colSpan}>
                  <UnstyledFormLabel>{t("productCosts")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="freightCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={colSpan}>
                  <UnstyledFormLabel>{t("freightCost")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="fulfillmentCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={colSpan}>
                  <UnstyledFormLabel>{t("fulfillmentCost")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <FormField
          control={form.control}
          name="transactionFees"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={colSpan}>
                  <UnstyledFormLabel>{t("transactionFees")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr className="border-b">
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell>{transactionFees}</NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell colSpan={colSpan} className="font-bold">
            {t("grossProfit")}
          </LabelCell>
        </Tr>
        <Tr className="border-b">
          <td />
          <NumberDisplayCell className="font-bold">
            {grossProfit}
          </NumberDisplayCell>
        </Tr>
        <Tr>
          <LabelCell className="font-bold">{t("breakEvenMerLabel")}</LabelCell>
        </Tr>
        <Tr className="border-b">
          <td />
          <NumberDisplayCell className="font-bold">
            {breakevenMer}
          </NumberDisplayCell>
        </Tr>
      </tbody>
      <tfoot className="py-4">
        <Tr>
          <td colSpan={2} className="px-5 py-3 text-end">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={() => {
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}

function DesktopTable({
  form,
  transactionFees,
  grossProfit,
  breakevenMer,
  t,
  tCommon,
}: ContentProps) {
  return (
    <Table className="hidden md:table">
      <colgroup>
        <col className="w-8/12" />
        <col className="w-2/12" />
        <col className="w-2/12" />
      </colgroup>
      <tbody className="bg-surface">
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell className="italic" colSpan={3}>
            {t("minusVariableCosts")}
          </LabelCell>
        </Tr>

        <FormField
          control={form.control}
          name="productCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("productCosts")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="freightCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("freightCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="fulfillmentCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("fulfillmentCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="transactionFees"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b">
                <LabelCell>
                  <UnstyledFormLabel>{t("transactionFees")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell>{transactionFees}</NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr className="border-b">
          <LabelCell className="font-bold">{t("grossProfit")}</LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {grossProfit}
          </NumberDisplayCell>
        </Tr>

        <Tr className="border-b">
          <LabelCell className="font-bold">{t("breakEvenMerLabel")}</LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {breakevenMer}
          </NumberDisplayCell>
        </Tr>
      </tbody>
      <tfoot className="py-4">
        <Tr className="border-x-0 border-b-0 md:border-x-0">
          <td colSpan={3} className="px-5 py-3 text-center">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={() => {
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}
